<template>
  <lf-modal :title="$t('PLACEMENT.SUBMIT_APPLICATION')" :close="close">
    <template #content>
      <form data-cy="submit-application-form" @submit="onSubmit">
        <div class="w-full items-baseline border-b pl-3">
          <div class="flex">
            <lf-tab
              v-for="funderNameRecord in funderNames"
              :key="funderNameRecord.id"
              :active="activeTab === funderNameRecord.id"
              class="w-1/4 font-bold"
              @click="handleFunderChange(funderNameRecord.id)"
            >
              {{ funderNameRecord.full_name }}
            </lf-tab>
          </div>
        </div>
        <div class="flex flex-1">
          <div class="w-56 items-baseline space-y-5 border-r border-b px-4">
            <div class="mt-3 flex flex-col space-y-2">
              <div
                v-for="product in funderProducts"
                :key="product?.id"
                class="ml-1 cursor-pointer rounded-sm p-3 text-gray-700"
                :class="{ 'bg-blue-100': sideTab?.id === product?.id }"
                @click="sideTab = product"
              >
                {{ product?.name }}
              </div>
            </div>
          </div>

          <div class="flex flex-1 flex-col overflow-hidden p-5 break-words">
            <div class="flex flex-1 items-baseline pb-3">
              <label class="w-16 font-light whitespace-nowrap text-headline">
                {{ $t("SUBMISSIONS.SEND_TO") }}
              </label>
              <div class="flex flex-wrap items-center">
                <lf-badge
                  v-for="email in funderEmailAddresses"
                  :key="email"
                  removable
                  is-rounded
                  data-cy="funder-email-badge"
                  @click:remove="removeEmail(email)"
                >
                  {{ email }}
                </lf-badge>
                <div
                  v-show="error"
                  class="px-2 text-left text-xxs text-red-700 sm:text-xs"
                >
                  {{ $t("SUBMISSIONS.ERROR") }}
                </div>
                <pill-dropdown
                  :available-emails="availableFunderProductEmails"
                  :excluded-emails="currentTabExclusionList"
                  data-cy="funder-email-pill-dropdown"
                  @save="saveEmailChanges"
                />
              </div>
            </div>

            <div class="flex flex-1 items-baseline pb-3">
              <label class="w-16 font-light whitespace-nowrap text-headline">
                {{ $t("SUBMISSIONS.CC") }}
              </label>
              <div class="flex flex-wrap">
                <lf-badge is-rounded>submissions@lendflow.io</lf-badge>
                <lf-badge v-if="fundingAdvisorEmail" is-rounded>
                  {{ fundingAdvisorEmail }}
                </lf-badge>
                <lf-badge v-if="underwriterEmail" is-rounded>
                  {{ underwriterEmail }}
                </lf-badge>
              </div>
            </div>
            <div
              v-if="deal.workflow_snapshot.has_whitelabeled_email_submission"
              class="pt-4 font-semibold text-headline"
              data-cy="workflow-controls-email"
            >
              {{ $t("SUBMISSIONS.EMAIL_DEFINED_IN_WORKFLOW") }}
            </div>

            <div
              v-show="!deal.workflow_snapshot.has_whitelabeled_email_submission"
            >
              <div
                class="flex flex-1 items-baseline overflow-hidden pb-3"
                data-cy="subject"
              >
                <label
                  class="w-16 shrink-0 font-light whitespace-nowrap text-headline"
                >
                  {{ $t("SUBMISSIONS.SUBJECT") }}
                </label>

                <div class="overflow-hidden text-ellipsis">
                  <label
                    class="w-16 font-light whitespace-nowrap text-headline"
                  >
                    {{ emailSubject }}
                  </label>
                </div>
              </div>

              <lf-text-editor
                data-cy="email-body"
                name="email_body"
                :value="emailBody"
                :disabled="
                  deal.workflow_snapshot.has_whitelabeled_email_submission
                "
              />
            </div>

            <attach-documents-modal
              v-model="attachments"
              :files-by-category="filesByCategory"
              :bank-statment-types="bankStatmentTypes"
            />
          </div>
        </div>
        <hr class="relative bottom-px h-2" />
        <div class="flex justify-end space-x-5 p-6">
          <outline-button @click="close">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <primary-button
            type="submit"
            data-cy="submit-button"
            :disabled="isSubmitting || !funderEmailAddresses.length"
          >
            <span>{{ $t("COMMON.SUBMIT") }}</span>
            <span
              class="ml-2 h-5 w-5 items-center justify-center rounded-full bg-white/20"
            >
              {{ selectedFunders?.length || 0 }}
            </span>
          </primary-button>
        </div>
      </form>
      <loader :is-loading="isSubmitting" />
    </template>
  </lf-modal>
</template>
<script lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import * as Sentry from "@sentry/vue";
import { useForm } from "vee-validate";

import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import {
  CATEGORY_DEAL_SUBMIT_APPLICATION,
  MESSAGE_SUBMIT_APPLICATION,
} from "@/helpers/constants/sentry";
import { PRODUCT_TYPE_REVERSE_MAPPING } from "@/helpers/constants/workflow";
import { sortFilesByCategory } from "@/helpers/files";
import { dispatchAction } from "@/helpers/vee-validate";
import { useDealFiles } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useActiveStage } from "@/hooks/workflow";
import useNotesStore from "@/stores/notes";

import type { IApplication } from "@/models/applications";
import type { IFunder, OfferTemplateRecord } from "@/models/funders";
import type { FileTypeCollection } from "@/models/options";
import type { FinProduct } from "@/models/workflows";
import type { Delta } from "@vueup/vue-quill";
</script>
<script setup lang="ts">
import AttachDocumentsModal from "@/components/modals/AttachDocumentsModal.vue";
import LfBadge from "@/components/ui/LfBadge.vue";
import LfTextEditor from "@/components/ui/LfTextEditor.vue";
import Loader from "@/components/ui/Loader.vue";
import LfModal from "@/components/ui/Modal.vue";
import PillDropdown from "@/components/ui/PillDropdown.vue";
import OutlineButton from "@/components/ui/buttons/OutlineButton.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";

const props = defineProps<{
  deal: IApplication;
  selectedFunders: IFunder[];
  close: () => void;
}>();

const emit = defineEmits<{
  applicationSubmitted: [value: number];
  clearSelectedRows: [];
}>();

const store = useStore();
const { activeStage } = useActiveStage();
const { t } = useI18n();
const { files, getDealFiles } = useDealFiles(props.deal.id);
const notesStore = useNotesStore();

const { showMessage } = useNotification();

const emailBody = computed<InstanceType<typeof Delta>>(
  () => store.getters["applications/placementEmailContent"] ?? ""
);

const { handleSubmit, isSubmitting } = useForm({
  initialValues: {
    email_body: emailBody.value,
  },
});

const fundingAdvisorEmail = computed(
  () => props.deal?.funding_advisor?.email || ""
);

const currentTabExclusionList = computed(() =>
  Array.from(getExcludedEmailList())
);

const underwriterEmail = computed(() => props.deal?.underwriter?.email || "");

const attachments = ref<number[]>([]);
const excludedEmails = ref<Record<string, Record<string, Set<string>>>>({});

const funderNames = computed(() => {
  return Array.from(
    props.selectedFunders
      .reduce((map, funder) => {
        // Our enriched funders already have the correct base funder ID
        if (!map.has(funder.id)) {
          map.set(funder.id, { full_name: funder.full_name, id: funder.id });
        }
        return map;
      }, new Map())
      .values()
  );
});

const activeTab = ref(funderNames.value[0].id);

const sideTab = ref<OfferTemplateRecord>();

const activeFunder = computed(() => {
  return props.selectedFunders.find((funder) => {
    return (
      funder.id === activeTab.value &&
      sideTab.value?.id === funder.offer_template?.id
    );
  });
});

const funderProducts = computed(() => {
  const activeFunderRecords = props.selectedFunders.filter((funder) => {
    return funder.id === activeTab.value;
  });

  return activeFunderRecords
    .map((funder) => funder.offer_template)
    .filter(Boolean) as OfferTemplateRecord[];
});

const emailSubject = computed(() => {
  if (!activeTab.value) {
    return "";
  }
  const name = "";
  const businessName = props.deal.business?.business_legal_name || "";

  return `${name} Submission: ${businessName}`;
});

const funderEmailAddresses = computed(() => {
  if (!activeFunder.value) {
    return [];
  }
  return availableFunderProductEmails.value.filter(
    (email) => !currentTabExclusionList.value.includes(email)
  );
});

const fileTypes = computed<Record<string, FileTypeCollection>>(
  () => store.getters["options/fileTypes"]
);
const filesByCategory = computed(() =>
  sortFilesByCategory(files.value, fileTypes.value)
);

const bankStatmentTypes = computed(() =>
  Object.values(fileTypes.value).find((file) => file.dir === "BANK_STATEMENTS")
);

const error = computed(() => funderEmailAddresses.value.length === 0);

const saveEmailChanges = (newExcludedEmails: string[]) => {
  if (!sideTab.value) {
    return;
  }
  excludedEmails.value[activeTab.value][sideTab.value.id] = new Set(
    newExcludedEmails
  );
};

const availableFunderProductEmails = computed(() => {
  if (
    !activeFunder.value ||
    !sideTab.value ||
    !activeFunder.value.email_addresses
  ) {
    return [];
  }

  const activeProductType =
    sideTab.value.product_type ||
    ((sideTab.value as unknown as Record<string, unknown>)
      .product as FinProduct);

  if (!activeProductType) {
    return [];
  }

  const productTypeKey = PRODUCT_TYPE_REVERSE_MAPPING[activeProductType];
  const emailsForProduct =
    activeFunder.value.email_addresses[productTypeKey] ?? [];
  const emailsForAllProductTypes =
    activeFunder.value.email_addresses["all"] ?? [];

  return [...emailsForProduct, ...emailsForAllProductTypes];
});

const getSelectedFunderEmails = (funder: IFunder) => {
  const selectedProductName = funder.offer_template?.product;
  const selectedProductId = funder.offer_template?.id;
  if (!selectedProductName || !selectedProductId) {
    return [];
  }
  const productType = PRODUCT_TYPE_REVERSE_MAPPING[selectedProductName];
  const validProductEmails = funder.email_addresses[productType] ?? [];
  const allEmails = funder.email_addresses["all"] ?? [];

  return [...validProductEmails, ...allEmails].filter(
    (email) =>
      !excludedEmails.value[activeTab.value]?.[selectedProductId]?.has(email)
  );
};

const onSubmit = handleSubmit(async ({ email_body }, actions) => {
  if (!activeStage.value?.id) {
    return showMessage(t("DEALS.PLACEMENT.NO_ACTIVE_STAGE_ERROR"), "error");
  }
  if (!props.selectedFunders?.length) {
    return;
  }

  await notesStore.getNotes({
    application_ids: [props.deal.id],
    per_page: DEFAULT_PER_PAGE,
  });

  const submissionPromises = props.selectedFunders.map(async (funder) => {
    const recipientEmails = getSelectedFunderEmails(funder);

    const productId =
      PRODUCT_TYPE_REVERSE_MAPPING[
        funder.offer_template?.product_type as FinProduct
      ];

    if (!recipientEmails.length) {
      throw new Error(`No recipient emails found for funder ${funder.id}`);
    }

    return dispatchAction(
      {
        email_body,
        attachments: attachments.value,
        funder_id: funder.id,
        emails: recipientEmails,
        method: 2,
        product_id: productId,
        stage_id: activeStage.value?.id,
        offer_template_id: funder.offer_template?.id,
        destination: {
          offer_template_id: funder.client_offer_template,
          workflow_template_id: funder.workflow_template_id,
        },
      },
      actions,
      "applications/createPlacement"
    );
  });

  let successfulSubmissions = 0;

  try {
    await Promise.all(submissionPromises);
    successfulSubmissions = props.selectedFunders.length;

    Sentry.addBreadcrumb({
      category: CATEGORY_DEAL_SUBMIT_APPLICATION,
      message: MESSAGE_SUBMIT_APPLICATION.replace(":app", props.deal.id),
    });
    showMessage(
      t("DEALS.PLACEMENT.EMAIL_SUBMISSIONS_SUCCESS", {
        count: successfulSubmissions,
      }),
      "success"
    );
    await store.dispatch("applications/getPlacements");
  } catch (err) {
    const error = err as { formattedErrors: string[]; message: string };
    const errorMessages = Object.values(error.formattedErrors || {}).flat();
    if (errorMessages.length) {
      showMessage(errorMessages.join(" - "), "error");
    } else if (error.message) {
      showMessage(error.message, "error");
    } else {
      showMessage(t("DEALS.PLACEMENT.EMAIL_SUBMISSION_ERROR"), "error");
    }
    return;
  }

  attachments.value = [];
  emit("clearSelectedRows");
  props.close();
  emit("applicationSubmitted", successfulSubmissions);
});

const removeEmail = (email: string) => {
  if (!sideTab.value) {
    return;
  }
  if (!excludedEmails.value[activeTab.value]?.[sideTab.value.id]) {
    if (!excludedEmails.value[activeTab.value]) {
      excludedEmails.value[activeTab.value] = {};
    }
    excludedEmails.value[activeTab.value][sideTab.value.id] = new Set([email]);
    return;
  }
  excludedEmails.value[activeTab.value][sideTab.value.id].add(email);
};

const getExcludedEmailList = () => {
  if (!sideTab.value) {
    return new Set<string>();
  }
  return excludedEmails.value[activeTab.value]?.[sideTab.value.id] || new Set();
};

const handleFunderChange = (funderId: string) => {
  activeTab.value = funderId;
  const firstProduct = funderProducts.value[0];
  if (firstProduct) {
    sideTab.value = firstProduct;
  }
};

onMounted(() => {
  getDealFiles();
  sideTab.value = funderProducts.value[0];
});
</script>
